import styles from "./userTableItem.module.scss";
import {
  useEffect,
  useMemo,
  useState
} from "react";
import okIcon from "../../shared/images/ok.svg";
import { ReactComponent as OkSvg } from "../../shared/images/ok.svg";
import { ReactComponent as CloseSvg } from "../../shared/images/close.svg";
import penIcon from "../../shared/images/pen.svg";
import trashIcon from "../../shared/images/trash.svg";
import { User } from "../../types/user.types";
import { useGetAllRolesQuery } from "../../api/services/role.service";
import { useTranslation } from "react-i18next";
import { MessageWindow } from "../../shared/components/messageWindow/messageWindow";
import { useDeleteUserMutation } from "../../api/services/user.service";
import { UserModal } from "../userModal/userModal";
import { useGetAllProjectsQuery } from "../../api/services/project.service";
import { Project } from "../../types/project.types";
import { mapProjects } from "../../utils/project.util";
import { mapRoles } from "../../utils/role.util";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import {hasDatePassed, isLessThanOneMonthAway} from "../../utils/date.utils";

type UserTableItemProps = {
  user: User;
  refetch: () => void;
}

export const UserTableItem = ({ user, refetch }: UserTableItemProps) => {
  const { t } = useTranslation();

  const { isTestToolAdmin } = useSelector((state: RootState) => state.authReducer);

  const [showDeleteMessage, setShowDeleteMessage] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);

  const { data: roles } = useGetAllRolesQuery("");
  const { data: projects } = useGetAllProjectsQuery("");
  const [ deleteUserMutation, { isSuccess: isSuccessDeleteUser } ] = useDeleteUserMutation();

  useEffect(() => {
    if (isSuccessDeleteUser) {
      refetch();
    }
  }, [isSuccessDeleteUser]);

  const onClickEdit = (): void => {
    setShowUserModal(true);
  }

  const onClickDelete = (): void => {
    setShowDeleteMessage(true);
  }

  const closeMessageWindow = (isConfirm: boolean): void => {
    if (isConfirm) {
      deleteUserMutation(user.id as number);
    }
    setShowDeleteMessage(false);
  }

  const onCloseUserModal = (isSave: boolean): void => {
    if (isSave) {
      refetch();
    }
    setShowUserModal(false);
  }

  const userRoles = useMemo(() => {
    return mapRoles(user.projects, roles);
  }, [roles, user.projects]);

  const userProjects: Array<Project> = useMemo(() => {
    return mapProjects(user.projects, projects);
  }, [projects, user.projects])

  const userPasswordExpirationDates = useMemo(() => {
    return user.projects.map(project => new Date(project.passwordExpirationDate).toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    }));
  }, [user.projects]);

  const lastActivityDate = useMemo(() => {
    return !!user.lastActivityDate ? new Date(user.lastActivityDate).toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    }) : "";
  }, [user.lastActivityDate]);

  const userActives = useMemo(() => {
    return user.projects.map(project => project.isActive);
  }, [user.projects]);

  return (
    <>
      <div className={styles.container}>
        <div className={styles.item}>
          <div className={styles.login}
               title={user.login}>
            <span>{user.login}</span>
          </div>
        </div>
        <div className={styles.item}
             title={user.email}>
          <span>{user.email}</span>
        </div>
        <div className={styles.item}
             title={user.fullName}>
          <span>{user.fullName}</span>
        </div>
        <div className={styles.item}>
          <div className={styles.projects}>
            {userProjects.map((project, index) => (
              <span key={index} title={project.name}>
                {project.name}
              </span>
            ))}
          </div>
        </div>
        <div className={styles.item}>
          <div className={styles.projects}>
            {userRoles.map((role, index) => (
              <span key={index}
                    title={role.name}>
                {role.name}
              </span>
            ))}
          </div>
        </div>
        <div className={styles.item}>
          <div className={styles.projects}>
            { user.projects.map((project, index) => {
              const passwordExpirationDate = new Date(project.passwordExpirationDate);
              const expirationString = passwordExpirationDate.toLocaleDateString('en-US', {
                month: '2-digit',
                day: '2-digit',
                year: 'numeric'
              });
              return (
                <span key={index}
                      className={`${isLessThanOneMonthAway(passwordExpirationDate) ? styles.yellow : ""} ${hasDatePassed(passwordExpirationDate) ? styles.red : ""}`}
                      title={expirationString}>
                {expirationString}
              </span>
              )
            })}
          </div>
        </div>
        <div className={styles.item}
             title={lastActivityDate}>
          <span>{lastActivityDate}</span>
        </div>
        <div className={styles.item}>
          <div className={styles.projects}>
            {userActives.map((active, index) => (
              <div className={styles.active} key={index}>
                {active
                  ? <img src={okIcon} alt=""/>
                  : <CloseSvg/>
                }
              </div>
            ))}
          </div>
        </div>
        <div className={styles.item}>
          <div className={styles.deleted}>
            {user.isDeleted && <OkSvg/>}
          </div>
        </div>
        <div className={styles.item}>
          {!user.isDeleted && (
            <div className={styles.actions}>
              <div className={styles.action} onClick={onClickEdit}>
                <img src={penIcon} alt=""/>
              </div>
              { isTestToolAdmin && (
                <div className={styles.action} onClick={onClickDelete}>
                  <img src={trashIcon} alt=""/>
                </div>
              ) }
            </div>
          )}
        </div>
      </div>
      {showDeleteMessage && (
        <MessageWindow onClose={closeMessageWindow}
                       title={t("user.delete")}
                       description={t("user.deleteDescription")}
                       primaryText={t("common.ok")} secondaryText={t("common.cancel")}/>
      )}
      {showUserModal && (
        <UserModal onClose={onCloseUserModal} user={user}/>
      )}
    </>

  );
}